import axios from "axios";

const kinoCache = new Map();
const CACHE_DURATION = 10 * 60 * 1000;

// Un titre promotionnel ne garantit pas une vraie bande-annonce.
const excludedTitle =
    /\b(teaser|clips?|featurette|parody|parodie|compilation|preview)\b|\bfan[\s-]*made\b|\b(?:tv\s*)?spots?\b|\bsuper\s*bowl\b|\bsneak\s*peek\b|\bbehind\s+the\s+scenes\b/i;

export const fetchKinoCheckTrailers = ({ tmdbId, language }) => {
    const movieId = Number(tmdbId);

    if (!Number.isSafeInteger(movieId) || movieId <= 0) {
        return Promise.resolve([]);
    }

    // Allemand pour DE ; anglais pour FR, EN et ES.
    const apiLanguage =
        String(language || "en").toLowerCase().split(/[-_]/)[0] === "de"
            ? "de"
            : "en";

    const cacheKey = movieId + ":" + apiLanguage;
    const cached = kinoCache.get(cacheKey);

    if (cached && cached.expiresAt > Date.now()) {
        return cached.promise;
    }

    const entry = { expiresAt: Date.now() + CACHE_DURATION };

    entry.promise = axios.get("https://api.kinocheck.com/movies", {
        params: {
            tmdb_id: movieId,
            language: apiLanguage,
            categories: "Trailer",
            limit: 100,
        },
        timeout: 7000,
    }).then(({ data }) => {
        if (data?.message === "movie not found") return [];

        if (data?.error) {
            throw new Error(
                data.message || "Erreur renvoyée par KinoCheck."
            );
        }

        // Aucun rapprochement approximatif par titre.
        if (Number(data?.tmdb_id) !== movieId) {
            throw new Error(
                "Identifiant TMDB inattendu dans la réponse KinoCheck."
            );
        }

        const seenKeys = new Set();
        const videos = [
            ...(Array.isArray(data.videos) ? data.videos : []),
            data.trailer,
        ];

        return videos.filter((video) => {
            if (
                !video ||
                !/^[A-Za-z0-9_-]{11}$/.test(video.youtube_video_id || "") ||
                video.language !== apiLanguage ||
                !Array.isArray(video.categories) ||
                video.categories.length !== 1 ||
                video.categories[0] !== "Trailer" ||
                excludedTitle.test(video.title || "") ||
                seenKeys.has(video.youtube_video_id)
            ) {
                return false;
            }

            seenKeys.add(video.youtube_video_id);
            return true;
        }).map((video) => ({
            id: "kinocheck-" + video.id,
            key: video.youtube_video_id,
            name: video.title || "",
            site: "YouTube",
            type: "Trailer",
            iso_639_1: video.language,
            published_at: video.published,
            movieFrenzySource: "KinoCheck",
            movieFrenzyLanguage: video.language,
            movieFrenzyCandidateLevel: "fallback",
            movieFrenzyOriginalSubtitled: /\bomu\b/i.test(video.title || ""),
        }));
    }).catch((error) => {
        const notFound =
            error.response?.data?.message === "movie not found";

        if (!notFound) {
            // Un incident temporaire expire plus vite qu'un résultat normal.
            entry.expiresAt = Date.now() + 60 * 1000;

            console.warn("[MovieFrenzy KinoCheck]", {
                movieId,
                language: apiLanguage,
                status: error.response?.status,
                message: error.message,
            });
        }

        return [];
    });

    kinoCache.set(cacheKey, entry);

    // Plafonner le cache conservé dans cet onglet.
    if (kinoCache.size > 100) {
        kinoCache.delete(kinoCache.keys().next().value);
    }

    return entry.promise;
};